/**
 * First run — name the household and its eaters (#1312).
 *
 * Two eaters by default because that is the product's name and its case, but
 * the form adds rows: the spec has always been a multi-member household, and a
 * screen that hard-codes two would have to be rewritten the first time a third
 * person sits down.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { twoPlates, detectedTimezone } from "../api/twoPlates";
import { ApiError, session } from "../lib/api";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export default function Setup() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState(session.name() ? `${session.name()}'s kitchen` : "");
  const [shopDay, setShopDay] = useState(3);
  const [eaters, setEaters] = useState<string[]>(["", ""]);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: twoPlates.createHousehold,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["household"] });
      navigate("/taste", { replace: true });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not save your household"),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const named = eaters.map((n) => n.trim()).filter(Boolean);
    if (named.length === 0) {
      setError("Give at least one person a name");
      return;
    }
    create.mutate({
      name: name.trim() || "Our kitchen",
      shopDay,
      // ⚠️ Captured here, silently, so the very first plan prints the right
      // day. Without it the server formats dates in UTC and a Wednesday
      // evening shop prints as Thursday.
      timezone: detectedTimezone(),
      eaters: named.map((n) => ({ name: n })),
    });
  }

  return (
    <div className="mx-auto max-w-md px-5 py-10">
      <h1 className="text-2xl font-semibold tracking-tight">Who eats here?</h1>
      <p className="text-muted mt-2 text-sm">
        Names only for now. What each person eats comes next, one food at a time.
      </p>

      <form onSubmit={submit} className="mt-8 flex flex-col gap-3">
        <label className="text-muted text-xs uppercase tracking-wide" htmlFor="hh">
          Household
        </label>
        <input
          id="hh"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Our kitchen"
          className="bg-surface border-line tap rounded-lg border px-4 text-base outline-none focus:border-plate-b"
        />

        <p className="text-muted mt-4 text-xs uppercase tracking-wide">People</p>
        {eaters.map((value, i) => (
          <div key={i} className="flex items-center gap-3">
            <span
              aria-hidden
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ background: i % 2 === 0 ? "var(--color-plate-a)" : "var(--color-plate-b)" }}
            />
            <input
              value={value}
              aria-label={`Person ${i + 1}`}
              onChange={(e) =>
                setEaters((prev) => prev.map((v, j) => (j === i ? e.target.value : v)))
              }
              placeholder={i === 0 ? "You" : "Them"}
              className="bg-surface border-line tap w-full rounded-lg border px-4 text-base outline-none focus:border-plate-b"
            />
            {eaters.length > 1 && (
              <button
                type="button"
                aria-label={`Remove person ${i + 1}`}
                onClick={() => setEaters((prev) => prev.filter((_, j) => j !== i))}
                className="text-muted px-2 text-lg"
              >
                ×
              </button>
            )}
          </div>
        ))}
        {eaters.length < 8 && (
          <button
            type="button"
            onClick={() => setEaters((prev) => [...prev, ""])}
            className="text-muted self-start text-sm underline underline-offset-4"
          >
            Add another person
          </button>
        )}

        <label className="text-muted mt-4 text-xs uppercase tracking-wide" htmlFor="day">
          Shopping day
        </label>
        <select
          id="day"
          value={shopDay}
          onChange={(e) => setShopDay(Number(e.target.value))}
          className="bg-surface border-line tap rounded-lg border px-4 text-base outline-none focus:border-plate-b"
        >
          {DAYS.map((d, i) => (
            <option key={d} value={i}>
              {d}
            </option>
          ))}
        </select>
        <p className="text-muted text-xs">
          Plans run three days from here, because that is how long fresh food lasts.
          You can make them longer in Kitchen.
        </p>

        {error && (
          <p role="alert" className="text-warn mt-2 text-sm">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={create.isPending}
          className="bg-plate-b text-ground tap mt-6 rounded-lg font-semibold disabled:opacity-50"
        >
          {create.isPending ? "Saving…" : "Next: tastes"}
        </button>
      </form>
    </div>
  );
}
